import React from 'react';

import { ApiErrors } from '@betterment/js-runtime';
import { Box, FormikField, Input, type InputProps, Label, Text } from '@betterment/samba-react';
import { lowerCase as _lowerCase, reduce as _reduce, upperFirst as _upperFirst, isFunction } from 'es-toolkit/compat';
import { ErrorMessage, FormikHelpers, FormikValues } from 'formik';
import * as Yup from 'yup';

import { InputGroup, type InputGroupProps } from '../Shared/components-v2/input-group';

type FormFieldGroupProps = {
  fieldName: string;
  labelText?: string;
  children?: React.ReactNode | ((inputProps: { id: string; name: string }) => React.ReactNode);
  autoFocus?: boolean;
  disabled?: boolean;
  placeholder?: string;
  component?: React.ComponentType<InputProps>;
  ariaLabel?: string;
};

type InputGroupWithLabelAndErrorProps = FormFieldGroupProps &
  Omit<InputGroupProps, keyof FormFieldGroupProps | 'children' | 'as' | 'asChild'>;

export const InputGroupWithLabelAndError: React.FC<InputGroupWithLabelAndErrorProps> = ({
  fieldName,
  labelText,
  children,
  placeholder = '',
  component: Component = Input,
  disabled = false,
  autoFocus = false,
  ariaLabel,
  ...boxProps
}) => {
  const commonInputProps = { id: fieldName, name: fieldName };

  // If children is a function, call it with the common props
  const childContent = isFunction(children) ? children(commonInputProps) : children;

  // When using style-closet-react components (passed as children), they handle their own
  // Formik integration via useField. Using FormikField would cause useField to be
  // called twice for the same field, with the second call (from FormikField.ErrorMessage)
  // overwriting the validation function registered by the style-closet-react component.
  if (childContent) {
    return (
      <InputGroup {...boxProps}>
        {labelText && (
          <Label htmlFor={fieldName} css={{ marginBottom: 'space.100' }}>
            {labelText}
          </Label>
        )}
        {childContent}
        <ErrorMessage name={fieldName}>
          {(errorMessage: string) => (
            <Box css={{ display: 'flex' }}>
              <Text css={{ textStyle: 'text50', color: 'text.critical', display: 'block', mt: 'space.50' }}>
                {errorMessage}
              </Text>
            </Box>
          )}
        </ErrorMessage>
      </InputGroup>
    );
  }

  // Samba Input component with FormikField integration. This is the default case.
  return (
    <InputGroup {...boxProps}>
      <FormikField {...commonInputProps}>
        {labelText && <FormikField.Label>{labelText}</FormikField.Label>}
        <FormikField.Control css={{ marginBottom: 'space.100' }}>
          <Component aria-label={ariaLabel} disabled={disabled} autoFocus={autoFocus} placeholder={placeholder} />
        </FormikField.Control>
        <FormikField.ErrorMessage />
      </FormikField>
    </InputGroup>
  );
};

export type PickedFormikHelpers<T> = Pick<FormikHelpers<T>, 'setFieldTouched' | 'setFieldError'>;

export const applyApiErrorsToFormik = <Values extends FormikValues = FormikValues>(
  formik: PickedFormikHelpers<Values>,
  apiErrors: ApiErrors,
  options?: {
    applyMessageOnlyForKey?: string;
  }
) => {
  Object.keys(apiErrors.messages).forEach(key => {
    let error = '';
    if (options?.applyMessageOnlyForKey || key === 'base') {
      error = `${apiErrors.messages[key][0]}.`;
    } else {
      error = `${_upperFirst(_lowerCase(key))} ${apiErrors.messages[key][0]}.`;
    }
    void formik.setFieldTouched(key, true, false);
    void formik.setFieldError(key, error);
  });
};

export function buildErrorMessagesFromApiErrors(
  this: Yup.TestContext,
  apiErrors: ApiErrors,
  fieldPrefix?: string
): Yup.ValidationError[] {
  return _reduce<ApiErrors['messages'], Yup.ValidationError[]>(
    apiErrors.messages,
    (result, value, key) => {
      const path = fieldPrefix ? `${fieldPrefix}.${key}` : key;
      return result.concat(this.createError({ path, message: `${_upperFirst(_lowerCase(key))} ${value[0]}` }));
    },
    []
  );
}

export function createFieldValidator<FieldValue>(
  name: string,
  validator: Yup.Schema<FieldValue>,
  validateField: FormikHelpers<FormikValues>['validateField']
) {
  return {
    validate: (value: FieldValue) => {
      return validator
        .validate(value)
        .then(() => undefined)
        .catch(({ errors }: Yup.ValidationError) => errors[0]);
    },
    onBlur: () => {
      void validateField(name);
    },
  };
}
